import * as React from 'react';
import { Button } from '@chakra-ui/react';
import { 
  useReactTable, 
  getPaginationRowModel, 
  ColumnDef, 
  getCoreRowModel, 
  Table,
  PaginationState,  
} from "@tanstack/react-table";
import { BsPlus } from 'react-icons/bs';
import { DataTableProps, UseDataTableProps } from '../Table.types';
import { useColumnCell } from './useColumnCell';
import { useTableProperties } from './useTableProperties';
import IndeterminateCheckbox from '../../../components/ui/checkbox/Indeterminate';
import { stringToCamelCase } from '../../../utilities/others';

export function useDataTable({ tableData, propertyList }: UseDataTableProps) {    
  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const [data, setData] = React.useState(tableData);

  const properties = useTableProperties(propertyList);

  const tableColumns = React.useMemo<ColumnDef<any>[]>( // type this!
    () => [
      {
        id: 'select',
        accessorKey: '',
        header: ({ table }) => (
          <span>
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </span>
        ),
        cell: ({ row, column}) => (
          <div className="px-1" style={{ float: column.id === 'select' ? 'left' : 'none' }}>
            <span className='flex'>
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </span>
          </div>
        )
      },
      ...properties?.properties
      .filter(({ isHidden }) => !isHidden)
      .map(({ name, type }) => ({
        id: name,
        header: () => <span>{name}</span>,
        accessorKey: (stringToCamelCase(name)),
        // eslint-disable-next-line react-hooks/rules-of-hooks
        cell: (props: any) => useColumnCell({ props, type, updateTableData: handleDataChange })
      })),
      {
        header: () => (
          <Button
            size="xs"
            width="fit-content"
            leftIcon={<BsPlus />}
            variant="ghost"
            color={'rgb(99, 115, 129)'}
            onClick={() => properties.onPropertyAdd()}
          >
            New
          </Button>
          ),
        accessorKey: "new"
      }
    ],
    [properties]
  );
  
  function handleDataChange(data: any) {    
    setData(data);
  }

  function handleAddRow() {
    const newRow = {} as any;

    properties.properties.forEach((prop: any) => {
      if (prop.options) { // select, multi-select
        newRow[prop.name] = {
          value: "",
          options: prop.options,
          type: prop.type.toLowerCase()
        };
      } else {
        newRow[prop.name] = { type: prop.type.toLowerCase(), value: "" };
      }
    });      
    setData([ ...data, newRow]);
  }

  function reorderRow(draggedRowIndex: number, targetRowIndex: number) {
    data.splice(targetRowIndex, 0, data.splice(draggedRowIndex, 1)[0])
    setData([...data])
  }

  React.useEffect(() => {    
    if (tableData) {
      setData(tableData)
    }
  }, [tableData])
      
  const tableProps = useReactTable({
      data,
      state: {
        pagination,
      },
      columns: tableColumns,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      meta: {
        updateData: (rowIndex: number, columnId: number, value: any) => {            
          setData(old =>              
            old.map((row, index) => {
              if (index === rowIndex) {                  
                return {
                  ...old[rowIndex]!,
                  [columnId]: value,
                }
              }
              return row
            })
          )
        },
      },
      debugTable: true,
  }) as Table<DataTableProps>;

  return { tableProps, properties, handleAddRow, reorderRow };
}